import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { toast } from "react-toastify";
import { formatCurrency } from "@/utils/format";
import { useTranslation } from "react-i18next";
import {
    balanceAtom,
    challengeAtom,
    featureSectionAtom,
    feeAtom,
    platformAtom,
    themeAtom,
    userAtom,
} from "@/store/atoms";
import mt4LogoDarkURL from "/src/assets/FeatureSection/MT4Logo-dark.webp";
import mt4LogoLightURL from "/src/assets/FeatureSection/MT4Logo-light.svg";
import mt5LogoDarkURL from "/src/assets/FeatureSection/MT5Logo-dark.webp";
import tradeLogo from "/src/assets/FeatureSection/trademate.svg";
import mt5LogoLightURL from "/src/assets/FeatureSection/MT5Logo-light.svg";
import matchTraderDarkURL from "/src/assets/FeatureSection/MatchTraderLogo-dark.webp";
import matchTraderLightURL from "/src/assets/FeatureSection/MatchTraderLogo-light.svg";
import tradeLockerDarkURL from "/src/assets/FeatureSection/TradeLockerLogo-dark.webp";
import tradeLockerLightURL from "/src/assets/FeatureSection/TradeLockerLogo-light.svg";
// import featureSecBgGradientPoint from "/src/assets/FeatureSection/featuresecBgGradientPoint.svg";
import { cn } from "@/utils/utils";
import { motion } from "framer-motion";
import { landingMotionInView } from "@/utils/landingMotion";
import { Marquee } from "../ui/Marquee";
import TradeMateOfferBanner from "./TradeMateOfferBanner";
import { payments, type PaymentDiscountInfo } from "@/api/endpoints/payments";
import { publicApi } from "@/api/endpoints/public";
import {
    balanceDataOfInstantFunding,
    balanceDataOfInstantFundingLite,
    balanceDataOfOneStep,
    balanceDataOfTwoPhase,
} from "@/config/pricingTables";
import { getProgramRules } from "@/config/programRules";

const paymtentmethodTab1 = [
    { img: "/comparision/logo.svg" },
    { img: "/comparision/amex.svg" },
    { img: "/comparision/master.svg" },
    { img: "/comparision/afterpay.svg" },
    { img: "/comparision/bank-fill.svg" },
];

const paymtentmethodTab2 = [
    { img: "/comparision/logo2.svg" },
    { img: "/comparision/bitcoin.svg" },
    { img: "/comparision/litecoin.svg" },
    { img: "/comparision/tether.svg" },
    { img: "/comparision/eth.svg" },
    { img: "/comparision/bnb.svg" },
];
   
  

// All daily-loss / max-DD / profit-target percentages are sourced from
// @/config/programRules (which mirrors backend tradeCheckOption). Do not
// hardcode them here — change them in programRules.ts so dashboard and
// pricing stay in sync.
const twoPhaseRulesP1 = getProgramRules("twoPhase", 1);
const twoPhaseRulesP2 = getProgramRules("twoPhase", 2);
const onePhaseRules = getProgramRules("onePhase", 1);
const instantProRules = getProgramRules("instantFundingPro");
const instantLiteRules = getProgramRules("instantFundingLite");

const phaseStateDetailInitialValueOfTwoPhase = {
    state: 1,
    profitTarget: [[500, twoPhaseRulesP1.profitTarget], [250, twoPhaseRulesP2.profitTarget], []],
    dailyLossLimit: twoPhaseRulesP1.dailyLossLimit,
    maxTrailingDrawDown: twoPhaseRulesP1.trailingDrawdownLimitPercent,
    refundableFee: 100,
};

const phaseStateDetailInitialValueOfOnePhase = {
    state: 1,
    profitTarget: [[500, onePhaseRules.profitTarget]],
    dailyLossLimit: onePhaseRules.dailyLossLimit,
    maxTrailingDrawDown: onePhaseRules.trailingDrawdownLimitPercent,
    refundableFee: 100,
};

const phaseStateDetailInitialValueOfInstantFundingPro = {
    state: 1,
    profitTarget: [],
    dailyLossLimit: instantProRules.dailyLossLimit,
    maxTrailingDrawDown: instantProRules.trailingDrawdownLimitPercent,
    refundableFee: 100,
};

const phaseStateDetailInitialValueOfInstantFundingLite = {
    state: 1,
    profitTarget: [],
    dailyLossLimit: instantLiteRules.dailyLossLimit,
    maxTrailingDrawDown: instantLiteRules.trailingDrawdownLimitPercent,
    refundableFee: 100,
};



interface FeaturesSectionProps {
    hidePlatformsOnMobile?: boolean;
    home?: boolean;
    variant?: "pricing" | "funnel";
}

export default function PricingComparisionTable({
    hidePlatformsOnMobile = false,
    home = false,
    variant = "pricing",
}: FeaturesSectionProps) {
    // for development
    // useScrollRestoration();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [radio, setRadio] = useState<number>(0);
    const [phase, setPhase] = useState<number>(1);
    // Defaults to Trade Mate (5), not MT5 (2) — see platformTypeData below.
    const [platformType, setPlatformType] = useState<number>(5);
    const [payMethod, setPayMethod] = useState<"online" | "crypto" | "paytiko">("online");
    // Paytiko is admin-controlled. When enabled, an extra "Online (Paytiko)"
    // payment option is shown alongside the existing online (Copecart) option.
    const [paytikoEnabled, setPaytikoEnabled] = useState<boolean>(false);
    const [accountBanner, setAccountBanner] = useState<{ text: string; isActive: boolean } | null>(null);
    const [discountSettings, setDiscountSettings] = useState<PaymentDiscountInfo>({
        cryptoDiscountPercent: 0,
        discountText: "",
        isActive: false,
    });

    useEffect(() => {
        let cancelled = false;
        publicApi
            .getAccountBannerConfig()
            .then((res) => {
                if (cancelled) return;
                if (res?.data && res.data.isActive) {
                    setAccountBanner({ text: res.data.text, isActive: true });
                } else {
                    setAccountBanner(null);
                }
            })
            .catch(() => {
                if (!cancelled) setAccountBanner(null);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        payments
            .getActiveDiscountInfo()
            .then((res) => {
                if (cancelled) return;
                if (res?.success && res.data) {
                    setDiscountSettings(res.data);
                }
            })
            .catch((err) => {
                console.error("Error fetching discount:", err);
                if (!cancelled) {
                    setDiscountSettings({
                        cryptoDiscountPercent: 0,
                        discountText: "",
                        isActive: false,
                    });
                }
            });

        return () => {
            cancelled = true;
        };
    }, []);

    // Fetch the public Paytiko enable/disable flag so we know whether to offer
    // the "Online (Paytiko)" payment option.
    useEffect(() => {
        let cancelled = false;
        payments
            .getPaytikoSettings()
            .then((res) => {
                if (!cancelled) setPaytikoEnabled(Boolean(res?.success && res?.data?.isEnabled));
            })
            .catch(() => {
                if (!cancelled) setPaytikoEnabled(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);
    const [user] = useAtom(userAtom);
    const setFeatureSectionRef = useSetAtom(featureSectionAtom);
    const setBalance = useSetAtom(balanceAtom);
    const setFee = useSetAtom(feeAtom);
    const setPlatform = useSetAtom(platformAtom);
    const setChallenge = useSetAtom(challengeAtom);
    const featureSectionRef = useRef<HTMLDivElement | null>(null);

    const phaseData = [
        { text: t("One Phase"), value: 1, state: "onePhase", pricingGroup: "evaluation" },
        { text: t("Two Phase"), value: 2, state: "twoPhase", pricingGroup: "evaluation" },
        { text: t("Instant Pro"), value: 3, state: "instantFundingPro", pricingGroup: "instant" },
        { text: t("Instant Lite"), value: 4, state: "instantFundingLite", pricingGroup: "instant" },
    ];

    const selectedPhase = phaseData.find((ph) => ph.value === phase) || phaseData[0];
    const isInstantFundingPhase = selectedPhase.pricingGroup === "instant";

    const [phaseStateDetail, setPhaseStateDetail] = useState<{
        state: number;
        profitTarget: number[][] | null;
        dailyLossLimit: number;
        maxTrailingDrawDown: number;
        refundableFee: number | null;
    }>(() =>
        phaseStateDetailInitialValueOfOnePhase
    );

    const leverageTableValue = t("FX 1:100 • Commodities 1:20 • Indices 1:20 • Crypto 1:2");
    const dailyLossTableValue =
        phaseStateDetail.dailyLossLimit > 0
            ? `${phaseStateDetail.dailyLossLimit}%`
            : "—";
    const profitTargetPercent = getProgramRules(selectedPhase.state).profitTarget;

    

    const balanceData: number[][] = isInstantFundingPhase
        ? (selectedPhase.state === "instantFundingLite"
            ? balanceDataOfInstantFundingLite
            : balanceDataOfInstantFunding)
        : selectedPhase.state === "onePhase"
            ? balanceDataOfOneStep
            : balanceDataOfTwoPhase;
    const desktopGridTemplateColumns = `150px repeat(${balanceData.length}, minmax(132px, 1fr))`;
    const desktopMinWidth = `${150 + balanceData.length * 132}px`;

    useEffect(() => {
        if (featureSectionRef) {
            setFeatureSectionRef(featureSectionRef);
        }
    }, [featureSectionRef]);

    const handlePhase = (value: number) => {
        setPhase(Number(value));
        const nextPhase = phaseData.find((ph) => ph.value === value);

        if (nextPhase?.pricingGroup === "instant") {
            setPhaseStateDetail(
                nextPhase.state === "instantFundingLite"
                    ? phaseStateDetailInitialValueOfInstantFundingLite
                    : phaseStateDetailInitialValueOfInstantFundingPro
            );
            return;
        }

        if (nextPhase?.state === "onePhase") {
            setPhaseStateDetail(phaseStateDetailInitialValueOfOnePhase);
            return;
        }

        setPhaseStateDetail(phaseStateDetailInitialValueOfTwoPhase);
    };

    //   const handleSubPhase = (value: string) => {
    //     setSubPhase(String(value));
    //   };

    const handlePlatformType = (value: number) => {
        // Allow MT4 (1), MT5 (2), Trade Mate (5); MatchTrader (3) and TradeLocker (4) are coming soon
        if (value !== 3 && value !== 4) {
            setPlatformType(value);
        }
    };

    const getDisplayFee = (baseFee: number) => {
        if (payMethod === "crypto" && discountSettings.isActive) {
            return Math.ceil(baseFee * (1 - discountSettings.cryptoDiscountPercent / 100));
        }
        return baseFee;
    };

   

    const [, setMT4Logo] = useState<string>();
    const [, setMT5Logo] = useState<string>();
    const [, setMatchTraderLogo] = useState<string>();
    const [, setTradeLockerLogo] = useState<string>();
    const themeAtomValue = useAtomValue(themeAtom);
    const platformTypeData = [
        { text: t("Choose MT4"), value: 1, state: "mt4", img: mt4LogoDarkURL },
        { text: t("Choose MT5"), value: 2, state: "mt5", img: mt5LogoDarkURL },
        {
            text: t("Trade Mate"),
            value: 5,
            state: "trademate",
            img: tradeLogo
        },
        {
            text: t("Coming soon"),
            value: 3,
            state: "matchTrader",
            img: matchTraderDarkURL,
        },
        {
            text: t("Coming soon"),
            value: 4,
            state: "tradeLocker",
            img: tradeLockerDarkURL,
        },
    ];

    useEffect(() => {
        setMT4Logo(themeAtomValue === "dark" ? mt4LogoDarkURL : mt4LogoLightURL);

        setMT5Logo(themeAtomValue === "dark" ? mt5LogoDarkURL : mt5LogoLightURL);
        setMatchTraderLogo(
            themeAtomValue === "dark" ? matchTraderDarkURL : matchTraderLightURL
        );
        setTradeLockerLogo(
            themeAtomValue === "dark" ? tradeLockerDarkURL : tradeLockerLightURL
        );
    }, [themeAtomValue]);

    const handleStartTrade = async (selectedIdx: number = radio) => {
        setBalance(balanceData[selectedIdx][0] * 1000);
        const displayFee = getDisplayFee(balanceData[selectedIdx][1]);
        setFee(displayFee);
        const platformData = platformTypeData.find(
            (ph) => ph.value === platformType
        );
        const challengeType = phaseData.find((ph) => ph.value === phase);
        setPlatform(platformData?.state || "");
        setChallenge(challengeType?.state || "");
        localStorage.setItem("platform", platformData?.state || "");
        localStorage.setItem("challenge", challengeType?.state || "");
        localStorage.setItem("balance", `${balanceData[selectedIdx][0] * 1000}`);
        localStorage.setItem("fee", `${displayFee}`);
        localStorage.setItem("payMethod", payMethod);

        if (user?.email) {
            if (payMethod === "online") {
                try {
                    const platform = platformData?.state || "";
                    const challenge = challengeType?.state || "";
                    const balance = balanceData[selectedIdx][0] * 1000;
                    const amount = displayFee;

                    const payload: any = {
                        email: user.email,
                        amount,
                        platform,
                        balance,
                        challenge,
                        paymentMethod: "card",
                        currency: "USD",
                        returnUrl: `${window.location.origin}/payment-success`,
                        notes: `Payment for ${platform.toUpperCase()} ${challenge} Challenge`,
                        termsAccepted: true,
                    };

                    const response = await payments.createPaymentV2(payload);
                    if (response?.success) {
                        const trackId = response.data?.payment?.trackId;
                        const paymentUrl = response.data?.card?.checkout_url;
                        if (trackId) localStorage.setItem("paymentTrackId", trackId);
                        if (paymentUrl) {
                            window.location.href = paymentUrl;
                            return;
                        }
                    }
                    toast.error(t("Failed to create payment. Please try again."));
                } catch (error: any) {
                    toast.error(
                        error?.response?.data?.message ||
                        t("Payment creation failed. Please try again.")
                    );
                }
                return;
            }

            navigate("/checkout");
        } else {
            toast.warn(t("Please login first to start trade."));
            navigate("/login");
        }
    };

    /** Matches ExtendedBanner shell */
    const GLASS_CARD =
        "relative overflow-hidden rounded-[20px] py-4 px-4 md:px-6 border border-white/35 bg-gradient-to-b from-white/0 to-white/10";

    return (
        <div
            id="pricingComparison"
            ref={featureSectionRef}
            className={cn(
                "relative w-full pb-10 md:pb-20 font-nexa  text-white",
                home ? "" : ""
            )}
        >
           

            <div className="relative  w-full pt-10 ">
                {/* Top row: two separate cards */}
                <div className={`grid gap-3  ${variant == "funnel" ? "xl:px-0 px-4 max-w-[1240px] mx-auto" : "w-full "} `}>
                    <motion.div
                        className={cn(
                            `relative   ${GLASS_CARD}`,
                            ""
                        )}
                        {...landingMotionInView}
                    >
                        
                        <div className="relative z-10  flex md:flex-row flex-col md:items-center gap-3 ">
                            <div className="text-lg min-w-max md:text-xl text-white">
                                {t("Funding Type")}:
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-5 w-full">
                                {phaseData.map((option) => (
                                    <button
                                        key={option.state}
                                        type="button"
                                        onClick={() => handlePhase(option.value)}
                                        className={cn(
                                            "pricing-tab-btn md:min-w-[198px] rounded-[12px] w-full px-6 py-2 text-base md:text-xl transition-colors",
                                            phase === option.value
                                                ? "btn-gradient-left text-white"
                                                : "text-white/80 hover:text-white btn-register"
                                        )}
                                    >
                                        {option.text}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        className={cn(
                            `relative overflow-hidden  ${GLASS_CARD} backdrop-blur-md`,
                            "shadow-[0_0_50px_rgba(185,214,82,0.10)] "
                        )}
                        {...landingMotionInView}
                    >
                       
                        <div className="relative z-10 md:h-full flex flex-col md:items-start gap-5 ">
                            <div className="text-lg md:text-xl text-white">
                                {t("Payment Method")}
                            </div>
                            <div className="w-full flex md:flex-row flex-col md:items-center gap-5 ">
                                <button
                                    type="button"
                                    onClick={() => setPayMethod("online")}
                                    className={cn(
                                        "pricing-tab-btn md:min-w-[198px] gap-x-1 md:gap-x-2 rounded-[12px] w-full px-6 py-3 text-sm min-[400px]:text-xl md:text-2xl transition-colors",
                                        payMethod === "online"
                                            ? "btn-gradient-left text-white"
                                            : "text-white/80 hover:text-white btn-register"
                                    )}
                                >
                                    <img src={paymtentmethodTab1[0].img} alt="copercart logo" className="w-6 md:w-8 h-6" />
                                    {t("Copecart")}
                                    {paymtentmethodTab1.slice(1,).map((img: { img: string }) => (
                                        <img src={img.img} alt="payment method logo" className="w-4 h-4 md:w-6 md:h-6" />
                                    ))}
                                </button>
                                {paytikoEnabled && (
                                    <button
                                        type="button"
                                        onClick={() => setPayMethod("paytiko")}
                                        className={cn(
                                            "pricing-tab-btn md:min-w-[198px] gap-x-1 md:gap-x-2 rounded-[12px] w-full px-6 py-3 text-sm min-[400px]:text-xl md:text-2xl transition-colors",
                                            payMethod === "paytiko"
                                                ? "btn-gradient-left text-white"
                                                : "text-white/80 hover:text-white btn-register"
                                        )}
                                    >
                                        <img src={paymtentmethodTab1[0].img} alt="paytiko logo" className="w-6 md:w-8 h-6" />
                                        {t("Paytiko")}
                                        {paymtentmethodTab1.slice(1,).map((img: { img: string }) => (
                                            <img src={img.img} alt="payment method logo" className="w-4 h-4 md:w-6 md:h-6" />
                                        ))}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setPayMethod("crypto")}
                                    className={cn(
                                        "pricing-tab-btn gap-x-0.5 md:gap-x-1 md:min-w-[198px] w-full rounded-[12px] px-6 py-3 text-sm min-[400px]:text-xl md:text-2xl transition-colors",
                                        payMethod === "crypto"
                                            ? "btn-gradient-left text-white"
                                            : "text-white/80 hover:text-white btn-register"
                                    )}
                                >
                                    <img src={paymtentmethodTab2[0].img} alt="crypto logo" className="w-5 h-5 md:w-8 md:h-7" />
                                    {t("Crypto")}
                                    {paymtentmethodTab2.slice(1,).map((img: { img: string }) => (
                                        <img src={img.img} alt="crypto logo" className="w-5 h-5 md:w-8 md:h-7" />
                                    ))}
                                    {discountSettings.isActive && (
                                        <span className="text-gradient font-bold font-creato text-xs md:text-sm">
                                            ({t(discountSettings.discountText)})
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Second row: platforms wrapped in its own card */}
                <motion.div
                    className={cn(
                        `relative mt-3 overflow-hidden  ${variant == "funnel" ? "max-w-[1240px] mx-4 xl:mx-auto" : "mx-auto"}  ${GLASS_CARD} backdrop-blur-md`,
                        "shadow-[0_0_50px_rgba(185,214,82,0.10)]",
                        hidePlatformsOnMobile && "block"
                    )}

                    {...landingMotionInView}
                >
                   
                    <div className="relative z-10 grid grid-cols-2 gap-x-2.5 gap-y-5 md:gap-5 md:grid-cols-5">
                        {platformTypeData.map((ph) => {
                            const disabled = ph.value === 3 || ph.value === 4;
                            const selected = platformType === ph.value;
                            return (
                                <button
                                    key={ph.value}
                                    type="button"
                                    disabled={disabled}
                                    onClick={!disabled ? () => handlePlatformType(ph.value) : undefined}
                                    className={cn(
                                        "relative flex items-center justify-center gap-2 rounded-[12px]  px-6 py-2 text-base md:text-xl  transition-colors",
                                        selected
                                            ? "btn-gradient-left text-white"
                                            : "text-white/80 hover:text-white btn-register",
                                        disabled && "opacity-55 cursor-not-allowed"
                                    )}
                                >
                                    {ph.img ? (
                                        <img
                                            src={ph.img}
                                            alt=""
                                            className="h-10 w-auto object-contain"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <span className="whitespace-nowrap font-creato font-bold">
                                            {ph.text}
                                        </span>
                                    )}
                                    {disabled && (
                                        <span className="absolute left-1/2 min-w-min w-[100px] right-1/2 -translate-x-1/2 -top-2 rounded-full border border-[#DBD633]/60 bg-black/40 px-2 py-0.5 text-[10px] font-semibold text-white/80">
                                            {t("Coming Soon")}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </motion.div>

                {/* Mobile: stacked cards (pricing) / grid cards (funnel) */}
                <div
                    className={cn(
                        "z-10 block backdrop-blur-md relative mt-4 overflow-hidden",
                        variant === "funnel" ? "max-w-[1240px] mx-auto xl:px-0 px-4" : "md:hidden"
                    )}
                    
                >
                    <TradeMateOfferBanner showForTradeMate={platformType === 5} />
                    <div
                        className={cn(
                            "grid gap-4",
                            variant === "funnel" ? "md:grid-cols-2 xl:grid-cols-3" : "grid-cols-1"
                        )}
                    >
                        {balanceData.map(([sizeK, fee], idx) => {
                            const selected = radio === idx;
                            const size = sizeK * 1000;
                            const displayFee = getDisplayFee(fee);
                            const isLastTwo =
                                variant === "funnel" &&
                                balanceData.length >= 5 &&
                                idx >= balanceData.length - 2;

                            const rows = [
                                variant === "funnel"
                                    ? { label: t("Trading Period"), value: "∞ Unlimited" }
                                    : { label: t("Trading Period"), value: "∞" },
                                {
                                    label: t("Profit Target"),
                                    value: `${formatCurrency(
                                        size * (profitTargetPercent / 100)
                                    )}/${profitTargetPercent}%`,
                                },
                                {
                                    label: t("Maximum Daily Loss"),
                                    value: dailyLossTableValue,
                                },
                                { label: t("Maximum Loss"), value: `${phaseStateDetail.maxTrailingDrawDown}%` },
                                { label: t("Account Leverage"), value: leverageTableValue },
                                { label: t("Rewards"), value: t("Bi-Weekly") },
                                { label: t("Profit Split"), value: t("80/20") },
                                { label: t("Scaling"), value: t("Up to $2M") },
                            ] as const;

                            return (
                                <div
                                    key={sizeK}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setRadio(idx)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            setRadio(idx);
                                        }
                                    }}
                                    className={cn(
                                        `w-full text-left cursor-pointer !px-0 !py-0 ${GLASS_CARD} rounded-[18px] border border-white/15 backdrop-blur-sm overflow-hidden transition-colors`,
                                        selected && "ring-1 ring-[#DBD633]/50",
                                        variant === "funnel" && (isLastTwo ? "" : "")
                                    )}
                                >
                                    {/* Header */}
                                    <div
                                        className={cn(
                                            "flex items-center px-5 pt-6 pb-3",
                                            variant === "funnel"
                                                ? "justify-center flex-col gap-2"
                                                : "justify-between",
                                            variant === "funnel" &&
                                                selected &&
                                                "bg-gradient-to-r from-[#1CCDE6] to-[#DBD633]"
                                        )}
                                    >
                                        <span className="rounded-full font-normal border border-white/15 bg-white/20 px-3 py-1 text-[10px] text-white/75">
                                            {t("ACCOUNT SIZE")}
                                        </span>
                                        <div
                                            className={cn(
                                                variant === "funnel" ? "text-2xl md:text-[32px]" : "text-xl",
                                                "font-creato font-extrabold",
                                                selected ? cn("text-gradient-right", variant === "funnel" && "text-white") : "text-white"
                                            )}
                                        >
                                            ${size.toLocaleString()}
                                        </div>
                                    </div>

                                    {/* Body */}
                                    <div className={cn("mt-4 space-y-2", variant === "funnel" && "mt-3")}>
                                        {rows.map((r) => (
                                            <div
                                                key={r.label}
                                                className="flex items-center justify-between gap-4 border-t border-white/10 px-5 pt-2"
                                            >
                                                <span
                                                    className={cn(
                                                        variant === "funnel" ? "text-[12px]" : "text-sm",
                                                        "font-normal text-white/80"
                                                    )}
                                                >
                                                    {r.label}
                                                </span>
                                                <span
                                                    className={cn(
                                                        variant === "funnel" ? "text-[12px]" : "text-sm",
                                                        "font-creato font-bold text-white/70 text-right"
                                                    )}
                                                >
                                                    {r.value}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Footer */}
                                    <div className="mt-4 border-t border-white/10 pt-4 px-5 py-6">
                                        <div
                                            className={cn(
                                                variant === "funnel"
                                                    ? "text-2xl md:text-[40px] text-center"
                                                    : "text-2xl",
                                                "font-creato font-extrabold",
                                                selected ? "text-gradient-right" : "text-white"
                                            )}
                                        >
                                            {formatCurrency(displayFee)}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setRadio(idx);
                                                void handleStartTrade(idx);
                                            }}
                                            className={cn(
                                                "mt-3 w-full rounded-[12px] px-6 py-2.5 font-normal transition-colors",
                                                variant === "funnel" ? "text-sm px-3" : "text-base",
                                                selected
                                                    ? "btn-gradient-left text-white"
                                                    : "border border-white/20 bg-white/5 text-white hover:bg-white/10"
                                            )}
                                        >
                                            {t("Get Started")}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>


                {/* Table wrapped in its own background card */}
                <motion.div
                    className={` z-10 block backdrop-blur-md ${GLASS_CARD} relative mt-3   overflow-hidde ${variant === "funnel" ? "hidden" : "hidden md:block"} `}
                    {...landingMotionInView}
                >



                    {/* Desktop: table */}
                    <div className="relative z-10 hidden md:block overflow-x-auto ">
                        <div className="min-w-fit" style={{ minWidth: desktopMinWidth }}>
                            <TradeMateOfferBanner showForTradeMate={platformType === 5} />
                            {/* Header row */}
                            <div className="grid gap-0" style={{ gridTemplateColumns: desktopGridTemplateColumns }}>
                                <div className="px-4 py-4" />
                                {balanceData.map(([sizeK], idx) => {
                                    const selected = radio === idx;
                                    const size = sizeK * 1000;
                                    return (
                                        <button
                                            key={sizeK}
                                            type="button"
                                            onClick={() => setRadio(idx)}
                                            className={cn(
                                                "px-3 py-4 text-left transition-colors cursor-pointer",
                                                selected ? "bg-white/5" : "hover:bg-white/5"
                                            )}
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="rounded-full font-normal border border-white/15 bg-white/20 px-3 py-1 text-[10px]  text-white/75">
                                                    {t("ACCOUNT SIZE")}
                                                </span>
                                                
                                            </div>
                                            <div className={cn("mt-2 text-xl font-creato font-extrabold", selected ? "text-gradient-right" : "text-white")}>
                                                ${size.toLocaleString()}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Body rows */}
                            {[
                                { label: t("Trading Period"), value: () => "∞" },
                                {
                                    label: t("Profit Target"),
                                    value: (size: number) =>
                                        `${formatCurrency(
                                            size * (profitTargetPercent / 100)
                                        )}/${profitTargetPercent}%`,
                                },
                                { label: t("Maximum Daily Loss"), value: () => dailyLossTableValue },
                                { label: t("Maximum Loss"), value: () => `${phaseStateDetail.maxTrailingDrawDown}%` },
                                { label: t("Account Leverage"), value: () => leverageTableValue },
                                { label: t("Rewards"), value: () => t("Bi-Weekly") },
                                { label: t("Profit Split"), value: () => t("80/20") },
                                { label: t("Scaling"), value: () => t("Up to $2M") },
                            ].map((row, rIdx) => (
                                <div
                                    key={rIdx}
                                    className={cn("grid border-t border-white/10")}
                                    style={{ gridTemplateColumns: desktopGridTemplateColumns }}
                                >
                                    <div className="px-0 py-3 text-sm font-semibold text-white">
                                        {row.label}
                                    </div>
                                    {balanceData.map(([sizeK], idx) => {
                                        const selected = radio === idx;
                                        const size = sizeK * 1000;
                                        const v = typeof row.value === "function" ? row.value(size) : row.value;
                                        return (
                                            <button
                                                key={`${rIdx}-${sizeK}`}
                                                type="button"
                                                onClick={() => setRadio(idx)}
                                                className={cn(
                                                    "px-3 py-2 text-left text-sm  font-creato text-white/50 transition-colors cursor-pointer",
                                                    !selected && "hover:bg-white/5",
                                                    selected && "bg-white/5 text-white"
                                                )}
                                            >
                                                {v as any}
                                            </button>
                                        );
                                    })}
                                </div>
                            ))}

                            {/* Footer row (price + CTA) */}
                            <div className="grid border-t border-white/10" style={{ gridTemplateColumns: desktopGridTemplateColumns }}>
                                <div className="px-4 py-4 text-sm font-semibold text-white/70" />
                                {balanceData.map(([sizeK, fee], idx) => {
                                    const selected = radio === idx;
                                    const displayFee = getDisplayFee(fee);
                                    return (
                                        <div
                                            key={`cta-${sizeK}`}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => setRadio(idx)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    setRadio(idx);
                                                }
                                            }}
                                            className={cn(
                                                "px-3 py-4 text-left transition-colors cursor-pointer",
                                                selected ? "bg-white/5" : "hover:bg-white/5"
                                            )}
                                        >
                                            <div className={cn("text-xl  font-creato font-extrabold", selected ? "text-gradient-right" : "text-white")}>
                                                {formatCurrency(displayFee)}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setRadio(idx);
                                                    void handleStartTrade(idx);
                                                }}
                                                className={cn(
                                                    "mt-3 w-full rounded-[12px] px-2 py-2 text-sm font-normal transition-colors",
                                                    selected
                                                        ? "btn-gradient-left text-white"
                                                        : "border border-white/20 bg-white/5 text-white hover:bg-white/10"
                                                )}
                                            >
                                                {t("Get Started")}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>


                        </div>
                    </div>
                </motion.div>



                {/* marquee for funnel page  */}
                {variant === "funnel" && accountBanner?.isActive && (
                    <motion.div
                        className="w-full mt-16 mb-4 sm:mb-6 md:mb-8 overflow-hidden border border-white/15 bg-gradient-to-r from-[#1CCDE6] to-[#DBD633] shadow-[0_0_28px_rgba(28,205,230,0.18)]"
                        {...landingMotionInView}
                    >
                        <Marquee
                            pauseOnHover
                            className="[--duration:250s]  [--gap:10rem] py-2.5 md:py-3 bg-transparent"
                        >
                            {Array.from({ length: 12 }).map((_, i) => (
                                <span
                                    key={i}
                                    className="font-creato text-black font-bold text-xl md:text-[26px]  whitespace-nowrap"
                                >
                                    {accountBanner.text}
                                </span>
                            ))}
                        </Marquee>
                    </motion.div>
                )}

            
            </div>
        </div>
    )
}
